import axios from "axios";
import axiosInstance from "../axios/axiosInstance";
import ENV from "constants/env";
import { getActiveCompanyId } from "utils/companyContext";

const companyId = () => getActiveCompanyId();

const timeOffApi = axios.create({
    baseURL: ENV.TIMEOFF_BASE_URL || (window.location.origin + '/timeoff/api'),
    headers: { 'X-API-key': ENV.API_KEY },
});

export const getTimeOffBalances = async (employeeId) => {
    const cId = companyId();
    const res = await timeOffApi.get(`/balances?companyId=${cId}&employeeId=${employeeId}`);
    const data = res.data;
    return Array.isArray(data) ? data : (data?.data ?? []);
};

export const getEmployeeProfile = async (employeeId) => {
    return (await axiosInstance.get(`/companies/${companyId()}/employees/${employeeId}`)).data;
};

export const updateEmployeeProfile = async (employeeId, payload) => {
    return (await axiosInstance.patch(`/companies/${companyId()}/employees/${employeeId}`, payload)).data;
};

export const getPaystubs = async (employeeId, cursor = "0") => {
    const res = await axiosInstance.get(
        `/companies/${companyId()}/employees/${employeeId}/paystubs`,
        { params: { cursor } }
    );
    const data = res.data;
    return Array.isArray(data) ? data : (data?.data ?? []);
};

export const getCompanyPayrolls = async () => {
    const res = await axiosInstance.get(`/companies/${companyId()}/payrolls`, {
        params: { page: 1, pageSize: 1000 }
    });
    const data = res.data;
    return Array.isArray(data) ? data : (data?.data ?? []);
};

export const previewPayroll = async (payrollId) => {
    return (await axiosInstance.get(`/companies/${companyId()}/payrolls/${payrollId}/preview`)).data;
};

export const getPaystub = async (employeeId, payrollId) => {
    return (await axiosInstance.get(
        `/companies/${companyId()}/employees/${employeeId}/paystubs/${payrollId}`
    )).data;
};

export const downloadPaystub = async (employeeId, payrollId) => {
    return (await axiosInstance.get(
        `/companies/${companyId()}/employees/${employeeId}/paystubs/${payrollId}/download`
    )).data;
};

export const getEmployeeDocuments = async (employeeId, year) => {
    const params = { employeeId };
    if (year) params.year = year;
    const res = await axiosInstance.get(
        `/companies/${companyId()}/documents/employee_tax_documents`,
        { params }
    );
    const data = res.data;
    return Array.isArray(data) ? data : (data?.data ?? []);
};

export const getEmployeeDocument = async (documentId) => {
    return (await axiosInstance.get(
        `/companies/${companyId()}/documents/employee_tax_documents/${documentId}`
    )).data;
};

export const downloadEmployeeDocument = async (documentId) => {
    const res = await axiosInstance.get(
        `/companies/${companyId()}/documents/employee_tax_documents/${documentId}/download`,
        { responseType: "blob" }
    );
    return { url: URL.createObjectURL(res.data) };
};

export const getWorkplaces = async () => {
    const res = await axiosInstance.get(`/companies/${companyId()}/workplaces`, {
        params: { page: 1, pageSize: 100 }
    });
    const data = res.data;
    return Array.isArray(data) ? data : (data?.data ?? []);
};

export const changePassword = async (payload) => {
    return (await axiosInstance.post(`/auth/change-password`, payload)).data;
};

export const changeEmail = async (payload) => {
    return (await axiosInstance.post(`/auth/change-email`, payload)).data;
};

export const clockIn = async (employeeId, workplaceId) => {
    return (await axiosInstance.post(`/companies/${companyId()}/timecards/clock-in`, {
        employeeId,
        workplaceId: workplaceId || null,
    })).data;
};

export const clockOut = async (timecardId) => {
    return (await axiosInstance.patch(`/companies/${companyId()}/timecards/${timecardId}/clock-out`)).data;
};

export const addBreak = async (timecardId, paid, durationSeconds) => {
    return (await axiosInstance.patch(`/companies/${companyId()}/timecards/${timecardId}/break`, {
        paid,
        durationSeconds,
    })).data;
};

export const getTimecards = async (employeeId) => {
    const res = await axiosInstance.get(`/companies/${companyId()}/timecards`, {
        params: { employeeId }
    });
    return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
};

// ── Time Off ─────────────────────────────────────────────────────────────────
export const getMyTimeOffPolicies = async () => {
    const res = await timeOffApi.get(`/policies?companyId=${companyId()}`);
    const data = res.data;
    return Array.isArray(data) ? data : (data?.data ?? []);
};

export const getMyTimeOffRequests = async (employeeId) => {
    const res = await timeOffApi.get(`/requests?companyId=${companyId()}&employeeId=${employeeId}`);
    const data = res.data;
    return Array.isArray(data) ? data : (data?.data ?? data?.items ?? []);
};

export const submitTimeOffRequest = async (payload) => {
    return (await timeOffApi.post('/requests', { ...payload, companyId: companyId() })).data;
};

export const cancelTimeOffRequest = async (id) => {
    return (await timeOffApi.delete(`/requests/${id}`)).data;
};

export const getEmployeeBankAccounts = async (employeeId) => {
    try {
        const res = await axiosInstance.get(`/companies/${companyId()}/employees/${employeeId}/bank_accounts`, { params: { cursor: '0' } });
        const data = res.data;
        return Array.isArray(data) ? data : (data?.data ?? data?.items ?? []);
    } catch { return []; }
};

export const requestBenefitEnrollment = async (employeeId, companyBenefitId) => {
    return (await axiosInstance.post(`/companies/${companyId()}/benefits/employee`, {
        employeeId,
        companyBenefitId,
        active: false,
        pendingReview: true,
    })).data;
};

export const getMyBenefits = async (employeeId) => {
    try {
        const res = await axiosInstance.get(`/companies/${companyId()}/benefits/employee`, {
            params: { employeeId }
        });
        const data = res.data;
        return Array.isArray(data) ? data : (data?.data ?? data?.items ?? []);
    } catch { return []; }
};

export const getMyCompanyBenefitPlans = async () => {
    try {
        const res = await axiosInstance.get(`/companies/${companyId()}/benefits/company`);
        const data = res.data;
        return Array.isArray(data) ? data : (data?.data ?? data?.items ?? []);
    } catch { return []; }
};